import { useTranslation } from 'react-i18next';
import type { MilestoneStage, OrderMilestones } from '../../api/types';
import { Chip, Money, Note, Panel, Rail, Table, type Tone } from '../../ui';

const STATE_TONE: Record<MilestoneStage['state'], Tone> = { PAID: 'done', DUE: 'us', UPCOMING: 'lapse' };

/**
 * 30 / 30 / 30 / 10 — the milestone figures, read-only (docs/plans/milestone-payments).
 *
 * Shared by the admin order page and the retailer order page so the two always show the same
 * figures: it is a view of what `GET .../milestones` derived from verified proofs, and it
 * gates nothing — the API enforces every stop. A legacy order (`scheduled: false`) renders only
 * a one-line notice, or nothing at all when `quietWhenLegacy` is set.
 */
export function MilestoneSummary(
  { milestones, quietWhenLegacy = false }: { milestones: OrderMilestones; quietWhenLegacy?: boolean },
) {
  const { t } = useTranslation();
  const m = milestones;
  if (!m.scheduled) {
    return quietWhenLegacy ? null : <Note>{t('order.milestones.legacyNotice')}</Note>;
  }
  const current = m.stages.find((s) => s.sequence === m.currentSequence);

  return (
    <Panel title={t('order.milestones.title')}>
      <Rail
        items={[
          [t('order.milestones.agreedTotal'), <Money key="t" amount={m.agreedTotal} currency={m.currency} />],
          [t('order.milestones.currentMilestone'), current ? t(`order.milestones.gate_${current.gates}`, { n: current.sequence }) : t('order.milestones.allPaid')],
          [t('order.milestones.amountDueNow'), <Money key="d" amount={m.amountDueNow} currency={m.currency} />],
          [t('order.milestones.verified'), <Money key="v" amount={m.verified} currency={m.currency} />],
          [t('order.milestones.pending'), <Money key="p" amount={m.pending} currency={m.currency} />],
          [t('order.milestones.remaining'), <Money key="r" amount={m.remaining} currency={m.currency} />],
        ]}
      />
      <Table<MilestoneStage>
        rows={m.stages}
        rowKey={(s) => String(s.sequence)}
        columns={[
          { header: t('order.milestones.colMilestone'), render: (s) => t(`order.milestones.gate_${s.gates}`, { n: s.sequence }) },
          { header: t('order.milestones.colShare'), numeric: true, render: (s) => `${s.pct.replace(/\.?0+$/, '')}%` },
          { header: t('order.milestones.colAmount'), numeric: true, render: (s) => <Money amount={s.amount} currency={m.currency} /> },
          {
            header: t('order.milestones.colCumulative'),
            numeric: true,
            render: (s) => <Money amount={s.cumulativeAmount} currency={m.currency} />,
          },
          {
            header: t('order.milestones.colStatus'),
            render: (s) => (
              <Chip tone={STATE_TONE[s.state]}>
                {t(`order.milestones.status${s.state === 'PAID' ? 'Paid' : s.state === 'DUE' ? 'Due' : 'Upcoming'}`)}
              </Chip>
            ),
          },
        ]}
      />
    </Panel>
  );
}
